import {
  type QueryClient,
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { toast } from "sonner";
import { assistantName } from "../lib/assistant-name";
import { ASSISTANT_LOCKED, lockedMessage } from "../lib/handler-lock";
import { ChatApiError, chatRequest } from "./client";
import type {
  AiRule,
  Assistant,
  DirectoryPage,
  ContactsDirectory,
  Identity,
} from "../types";

const api = API_ENDPOINTS.webchat;
const assistantsKey = (identity: Identity) =>
  ["webchat", identity.clientId, identity.memberId, "assistants"] as const;

function reportLocked(
  client: QueryClient,
  identity: Identity,
  error: unknown,
  memberIds: string[],
) {
  if (!(error instanceof ChatApiError) || error.code !== ASSISTANT_LOCKED)
    return;
  const cached = client.getQueryData<{ assistants: Assistant[] }>(
    assistantsKey(identity),
  );
  const match = cached?.assistants.find((assistant) =>
    memberIds.includes(assistant.memberId),
  );
  toast.error(
    match
      ? lockedMessage(match, assistantName(undefined, [match]))
      : error.message,
  );
  void client.invalidateQueries({ queryKey: assistantsKey(identity) });
}
export const directoryKey = (identity: Identity) =>
  ["webchat", identity.clientId, identity.memberId, "directory"] as const;

export function directoryQueryOptions(identity: Identity) {
  return infiniteQueryOptions({
    queryKey: directoryKey(identity),
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      chatRequest<DirectoryPage>(
        `${api.chats}?${new URLSearchParams({ limit: "50", ...(pageParam ? { cursor: pageParam } : {}) })}`,
        "GET",
        undefined,
        signal,
      ),
    getNextPageParam: (page) => page.nextCursor || undefined,
    maxPages: 5,
    gcTime: 60_000,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** One bounded directory cache is shared by the launcher and realtime catch-up. */
export function useChatDirectory(identity: Identity) {
  return useInfiniteQuery(directoryQueryOptions(identity));
}

export function useContacts(identity: Identity) {
  return useQuery({
    queryKey: ["webchat", identity.clientId, identity.memberId, "contacts"],
    queryFn: ({ signal }) =>
      chatRequest<ContactsDirectory>(api.contacts, "GET", undefined, signal),
    staleTime: 30000,
    refetchInterval: false,
    gcTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useOpenConversation(identity: Identity) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (peerId: string) =>
      chatRequest<{ chatId: string }>(api.chats, "POST", { peerId }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: directoryKey(identity) }),
    onError: (error, peerId) => reportLocked(client, identity, error, [peerId]),
    networkMode: "always",
  });
}

export interface CreateGroup {
  reqKey: string;
  title: string;
  memberIds: string[];
  includeAssistant?: boolean;
  assistants?: { memberId: string; aiRule: AiRule }[];
}

export function useCreateGroup(identity: Identity) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGroup) =>
      chatRequest<{ chatId: string }>(api.groups, "POST", body),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: directoryKey(identity) }),
    onError: (error, body) =>
      reportLocked(
        client,
        identity,
        error,
        body.assistants?.map((assistant) => assistant.memberId) ?? [],
      ),
    networkMode: "always",
    retry: false,
  });
}

export function useContactAction(identity: Identity) {
  const client = useQueryClient();
  const queryKey = [
    "webchat",
    identity.clientId,
    identity.memberId,
    "contacts",
  ];
  return useMutation({
    mutationFn: (body: {
      memberId: string;
      action: "REQUEST" | "ACCEPT" | "DECLINE";
    }) => chatRequest<ContactsDirectory>(api.contacts, "POST", body),
    onSuccess: async (data) => {
      await client.cancelQueries({ queryKey });
      client.setQueryData(queryKey, data);
      await client.invalidateQueries({ queryKey: directoryKey(identity) });
    },
    networkMode: "always",
    retry: false,
  });
}

export function useAssistants(identity: Identity) {
  return useQuery({
    queryKey: assistantsKey(identity),
    queryFn: ({ signal }) =>
      chatRequest<{ assistants: Assistant[] }>(
        api.assistants,
        "GET",
        undefined,
        signal,
      ),
    staleTime: 30000,
  });
}
